import React, { useEffect, useState } from "react";
import {
  FaShoppingCart,
  FaUndo,
  FaBoxes,
  FaCheckCircle,
  FaClock,
  FaMoneyBillWave,
  FaChartBar,
  FaExclamationTriangle,
  FaUserTie,
  FaTruck,
  FaClipboardList,
} from "react-icons/fa";
import ExpiryAlertModal from "../../components/modals/ExpiryAlertModal";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { useNavigate } from "react-router-dom";

const DispenserDashboard = () => {
  const navigate = useNavigate();
  const { activeSession } = useGlobalContext();
  const { requestWithAuth } = useAuth();
  const [gettingDashboardData, setGettingDashboardData] = useState(false);
  const [gettingRecentSales, setGettingRecentSales] = useState(false);
  const [recentSales, setRecentSales] = useState([]);
  const [gettingRecentRequisitions, setGettingRecentRequisitions] =
    useState(false);
  const [recentRequisitions, setRecentRequisitions] = useState([]);
  const [showExpiryAlert, setShowExpiryAlert] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    today_sales: "0.00",
    sales_count: 0,
    pending_returns: 0,
    return_value: 0,
    pending_requisitions: 0,
    approved_requisitions: 0,
    warehouse_supplies_pending: 0,
    supply_value: 0,
    low_stock_items: 0,
    monthly_sales: 0,
    expiring_drugs: 0,
    expired_drugs: 0,
  });

  // Check if alert should be shown
  useEffect(() => {
    const checkExpiryAlert = () => {
      const today = new Date().toISOString().split("T")[0];
      const dismissedDate = localStorage.getItem("expiryAlertDismissed");
      const hasExpiryIssues =
        dashboardData?.expiring_drugs > 0 || dashboardData?.expired_drugs > 0;
      const shouldShow = hasExpiryIssues && dismissedDate !== today;

      if (shouldShow) {
        setTimeout(() => {
          setShowExpiryAlert(true);
        }, 500);
      }
    };

    if (!gettingDashboardData) {
      checkExpiryAlert();
    }
  }, [
    gettingDashboardData,
    dashboardData?.expiring_drugs,
    dashboardData?.expired_drugs,
  ]);

  const handleCloseExpiryAlert = () => {
    setShowExpiryAlert(false);
  };

  const handleCloseAndHideExpiryAlert = () => {
    if (dashboardData?.expired_drugs > 0) {
      toast.error(
        `You have ${dashboardData?.expired_drugs} expired drugs, please clear them before continuing`
      );
      return;
    }
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("expiryAlertDismissed", today);
    setShowExpiryAlert(false);
  };

  const handleViewExpiringDrugs = () => {
    const branch_id = activeSession?.branch?.id;
    if (dashboardData?.expired_drugs === 0) {
      const today = new Date().toISOString().split("T")[0];
      localStorage.setItem("expiryAlertDismissed", today);
    }
    setShowExpiryAlert(false);
    navigate(`/inventory/branch/expiring-drugs/${branch_id}`);
  };

  const getDispenserDashboardData = async () => {
    setGettingDashboardData(true);
    try {
      const response = await requestWithAuth(
        `/users/dispenser/dashboard`,
        "GET"
      );
      setDashboardData(response?.data);
    } catch (error) {
      toast.error("Failed to get dashboard data");
    } finally {
      setGettingDashboardData(false);
    }
  };

  const getRecentSales = async () => {
    setGettingRecentSales(true);
    try {
      const response = await requestWithAuth(
        `/users/dispenser/recent-sales`,
        "GET"
      );
      setRecentSales(response?.data);
    } catch (error) {
      toast.error("Failed to get recent sales data");
    } finally {
      setGettingRecentSales(false);
    }
  };

  const getRecentRequisitions = async () => {
    setGettingRecentRequisitions(true);
    try {
      const response = await requestWithAuth(
        `/users/dispenser/recent-requisitions`,
        "GET"
      );
      setRecentRequisitions(response?.data);
    } catch (error) {
      toast.error("Failed to get recent sales data");
    } finally {
      setGettingRecentRequisitions(false);
    }
  };

  useEffect(() => {
    getDispenserDashboardData();
    getRecentSales();
    getRecentRequisitions();
  }, []);

  const StatCard = ({
    title,
    value,
    subtitle,
    additionalInfo,
    icon: Icon,
    color,
    linkTo,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-white shadow-lg hover:shadow-xl transition-shadow`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="text-2xl opacity-80" />
        <div className="text-right">
          <h3 className="text-2xl font-bold">{value}</h3>
          <p className="text-sm opacity-90">{title}</p>
        </div>
      </div>
      {subtitle && <p className="text-xs opacity-75 mb-2">{subtitle}</p>}
      {additionalInfo && (
        <p className="text-xs opacity-75 mb-3">{additionalInfo}</p>
      )}
      {linkTo && (
        <button
          onClick={() => navigate(linkTo)}
          className="inline-flex items-center gap-1 text-sm hover:underline cursor-pointer"
        >
          Access <FaClipboardList className="text-[10px]" />
        </button>
      )}
    </div>
  );

  const SimpleCard = ({
    title,
    value,
    linkTo,
    color = "bg-gray-100",
    subtitle,
    icon: Icon,
  }) => (
    <div
      className={`rounded-lg p-6 ${color} text-center shadow-md hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-center mb-3">
        {Icon && <Icon className="text-3xl text-gray-600" />}
      </div>
      <h3 className="text-2xl font-bold mb-2 text-gray-800">{value}</h3>
      <p className="text-sm text-gray-600 mb-3">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      {linkTo && (
        <button
          onClick={() => (window.location.href = linkTo)}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline cursor-pointer"
        >
          View <FaClipboardList className="text-[12px]" />
        </button>
      )}
    </div>
  );

  if (gettingDashboardData) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <FaUserTie className="text-blue-600" />
            Dispenser Dashboard
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-gray-200 rounded-lg h-36 animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen ">
      <ExpiryAlertModal
        isOpen={showExpiryAlert}
        onClose={handleCloseExpiryAlert}
        onHideModal={handleCloseAndHideExpiryAlert}
        expiringDrugs={dashboardData.expiring_drugs}
        expiredDrugs={dashboardData.expired_drugs}
        onViewDetails={handleViewExpiringDrugs}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <FaUserTie className="text-blue-600" />
          Dispenser Dashboard
        </h1>
        <p className="text-gray-600">
          Point of sales, returns, and warehouse management
        </p>
      </div>

      {/* Core Functions */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <FaShoppingCart className="text-gray-600" />
          Core Dispenser Functions
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Primary sales and inventory operations
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Returns Request"
          value={dashboardData?.pending_returns}
          subtitle={`Total Value: GHS ${dashboardData?.return_value}`}
          additionalInfo="Pending return requests"
          icon={FaUndo}
          color="bg-orange-600"
          linkTo="/sales/item-return"
        />

        <StatCard
          title="Requisition to Warehouse"
          value={dashboardData?.pending_requisitions}
          subtitle={`${dashboardData?.approved_requisitions} approved this month`}
          additionalInfo="Request stock from warehouse"
          icon={FaBoxes}
          color="bg-purple-600"
          linkTo="/inventory/requisition"
        />

        <StatCard
          title="Authorize Supply"
          value={dashboardData?.warehouse_supplies_pending}
          additionalInfo="Approve warehouse supplies"
          icon={FaCheckCircle}
          color="bg-green-600"
          linkTo="/inventory/requisition"
        />
      </div>

      {/* Sales & Inventory Overview */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Sales & Inventory Overview
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Current sales performance and stock status
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <SimpleCard
          title="Total Drugs"
          value={`${dashboardData?.total_drugs}`}
          subtitle="Total items at this branch"
          linkTo={`/inventory/branch/${activeSession?.branch?.id}/`}
          color="bg-white border-2 border-blue-200 text-blue-800"
          icon={FaChartBar}
        />

        <SimpleCard
          title="Expiry Alert"
          value={dashboardData?.expiring_drugs}
          subtitle="Items that are about to expire"
          linkTo={`/inventory/branch/expiring-drugs/${activeSession?.branch?.id}`}
          color="bg-white border-2 border-red-200 text-red-800"
          icon={FaExclamationTriangle}
        />

        <SimpleCard
          title="Low Stock Alert"
          value={dashboardData?.low_stock_items}
          subtitle="Items requiring restock"
          linkTo="/inventory/reorder/level"
          color="bg-white border-2 border-red-200 text-red-800"
          icon={FaExclamationTriangle}
        />
      </div>

      {/* Recent Activity */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Recent Activity
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Latest transactions and requests
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FaClock className="text-blue-600 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Sales
            </h3>
          </div>
          <div className="space-y-3">
            {recentSales.map((sale, index) => (
              <div
                key={index}
                className="flex justify-between items-center pb-3 border-b"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Sale #{sale.invoice_number}
                  </p>
                  <p className="text-xs text-gray-500">{sale.time}</p>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => (window.location.href = "/sales/cashier/")}
            className="mt-4 text-sm text-blue-600 hover:underline cursor-pointer"
          >
            View all sales history →
          </button>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <FaBoxes className="text-purple-600 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Requisitions
            </h3>
          </div>
          <div className="space-y-3">
            {recentRequisitions.map((requisition, index) => (
              <div
                key={index}
                className="flex justify-between items-center pb-3 border-b"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {requisition.drug}
                  </p>
                  <p className="text-xs text-gray-500">{requisition.time}</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full ">
                  {requisition.status}
                </span>
              </div>
            ))}
          </div>
          <button
            onClick={() => (window.location.href = "/inventory/requisition")}
            className="mt-4 text-sm text-blue-600 hover:underline cursor-pointer"
          >
            View all requisitions →
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/pos/dispenser")}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaShoppingCart /> New Sale
          </button>
          <button
            onClick={() => navigate("/sales/item-return")}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaUndo /> Sale Returns
          </button>
          <button
            onClick={() => navigate("/inventory/requisition/new")}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaBoxes /> New Requisition
          </button>

          <button
            onClick={() => navigate("/inventory/stock")}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-700 transition-colors cursor-pointer flex items-center gap-2"
          >
            <FaClipboardList /> Check Inventory
          </button>
        </div>
      </div>
    </div>
  );
};

export default DispenserDashboard;
